/**
 * Text utility functions for display formatting
 */

/**
 * Strip markdown formatting from text for plain-text display.
 * Removes headers, bold, italic, code blocks, links, lists, etc.
 */
export const stripMarkdown = (text) => {
  if (!text || typeof text !== 'string') return '';

  return text
    // Remove code blocks (``` ... ```)
    .replace(/```[\s\S]*?```/g, '')
    // Remove inline code
    .replace(/`([^`]+)`/g, '$1')
    // Remove headers (# ## ### etc.)
    .replace(/^#{1,6}\s+/gm, '')
    // Remove bold (**text** or __text__)
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    // Remove italic (*text* or _text_)
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    // Remove links [text](url)
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // Remove images ![alt](url)
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
    // Remove horizontal rules
    .replace(/^---+$/gm, '')
    // Remove list markers
    .replace(/^[\s]*[-*+]\s+/gm, '')
    .replace(/^[\s]*\d+\.\s+/gm, '')
    // Remove blockquotes
    .replace(/^>\s+/gm, '')
    // Remove HTML tags
    .replace(/<[^>]+>/g, '')
    // Collapse multiple newlines
    .replace(/\n{3,}/g, '\n\n')
    // Collapse multiple spaces
    .replace(/  +/g, ' ')
    .trim();
};

/**
 * Truncate text to a maximum length, adding ellipsis if needed.
 */
export const truncateText = (text, maxLength = 200) => {
  if (!text || typeof text !== 'string') return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).replace(/\s+\S*$/, '') + '...';
};

/**
 * Strip markdown and truncate for preview display.
 */
export const previewText = (text, maxLength = 200) => {
  return truncateText(stripMarkdown(text), maxLength);
};

/**
 * Clean a product/project name by removing markdown formatting.
 * Handles: leading/inline markdown headers, spec prefixes, and long descriptions.
 */
export const cleanName = (name) => {
  if (!name || typeof name !== 'string') return '';

  let cleaned = name
    // Remove markdown header markers anywhere (## Overview -> Overview)
    .replace(/#{1,6}\s+/g, '')
    // Remove "Product Spec —" or "Product Specification —" prefixes
    .replace(/^Product\s+Spec(?:ification)?\s*[—–-]\s*/i, '')
    // Remove trailing "- Product Specification" suffixes
    .replace(/\s*[-—–]\s*Product\s+Spec(?:ification)?\s*$/i, '')
    // Collapse multiple spaces
    .replace(/\s{2,}/g, ' ')
    .trim();

  // If still very long (looks like a description, not a name), take first sentence/line
  if (cleaned.length > 80) {
    // Try to find a natural break point
    const firstLine = cleaned.split(/[.\n]/)[0].trim();
    if (firstLine.length > 5 && firstLine.length <= 80) {
      cleaned = firstLine;
    } else {
      cleaned = cleaned.substring(0, 77) + '...';
    }
  }

  return cleaned;
};
